import { useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { dataConstants } from 'services/redux/data/constants';

// A request settles before the next poll is scheduled. Each effect owns its responses.
export default function useForegroundPolling({ requestKey, request, interval = 30000, enabled = true }) {
	const isFocused = useIsFocused();
	const employee = useSelector( ( state ) => {
		return state.data[dataConstants.EMPLOYEE];
	});
	const accountKey = JSON.stringify([employee?.guid, employee?.user]);
	const key = JSON.stringify([accountKey, requestKey]);
	const requestRef = useRef(request);
	requestRef.current = request;
	const [ foreground, setForeground ] = useState( () => {
		return AppState.currentState !== 'background' && AppState.currentState !== 'inactive';
	});
	const [ visible, setVisible ] = useState( () => {
		return Platform.OS !== 'web' || typeof document === 'undefined' || !document.hidden;
	});
	const [ state, setState ] = useState({ key, data: null, error: null, loading: true });
	const [ attempt, setAttempt ] = useState(0);

	useEffect( () => {
		const subscription = AppState.addEventListener('change', ( next ) => {
			setForeground(next === 'active');
		});
		const onVisibility = () => {
			setVisible(!document.hidden);
		};
		if( Platform.OS === 'web' && typeof document !== 'undefined' ) {
			document.addEventListener('visibilitychange', onVisibility);
		}
		return () => {
			subscription.remove();
			if( Platform.OS === 'web' && typeof document !== 'undefined' ) {
				document.removeEventListener('visibilitychange', onVisibility);
			}
		};
	}, []);

	useEffect( () => {
		if( !enabled || !isFocused || !foreground || !visible ) {
			return;
		}
		let cancelled = false;
		let timer;
		const poll = () => {
			let settled = false;
			setState( ( previous ) => {
				return { key, data: previous.key === key ? previous.data : null, error: null, loading: true };
			});
			const finish = ( data, error ) => {
				if( cancelled || settled ) {
					return;
				}
				settled = true;
				setState({ key, data, error, loading: false });
				timer = setTimeout(poll, interval);
			};
			try {
				requestRef.current({
					callbackSuccess: ( data ) => {
						finish(data ?? {}, null);
					},
					callbackError: ( error ) => {
						finish(null, error ?? new Error('Request failed'));
					},
				});
			} catch( error ) {
				finish(null, error);
			}
		};
		poll();
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [ key, enabled, isFocused, foreground, visible, interval, attempt ]);

	return {
		...(state.key === key ? state : { data: null, error: null, loading: true }),
		retry: () => {
			setAttempt( ( value ) => {
				return value + 1;
			});
		},
	};
}
